import React, { useEffect, useMemo, useState, useCallback } from 'react'
import { useParams } from 'react-router'
import { Form, Flex } from 'antd'
import { ASSIGNMENT_IS_TOO_BIG_FOR_PROJECTION } from '../../../../constants/response-messages'
import { actions } from '../../../../store/reducers/assignment.reducer'
import { connect } from 'react-redux'
import Routes from '../../../../constants/routes'
import { AssignmentSections } from '../../../../constants/assignment-sections'
import useProjectionCostEstimation from '../../../../hooks/useProjectionCostEstimation'
import { useVersionId } from '../../../../utils/parseURL.helper'
import { VERSION_ID_PARAM } from '../../../../constants/query-params'
import { useNavigate } from 'react-router-dom'
import './discovery-descriptions-review.scss'
import _ from 'lodash'
import { LLM_VERSIONS } from '../../../../constants/llm-versions'
import { 
    useDeleteConceptualLabelDiscoveryMutation,
} from '../../../../store/apis/conceptual-label.api'
import { usePeriodicSaving } from './hooks/usePeriodicSaving'
import usePageTransition from '../../../../hooks/usePageTransition'
import PatternList from './components/PatternList'
import CreatePatternsButton from './components/CreatePatternsButton'
import FilterPatterns from './components/FilterPatterns'


/**
 * Review section for discovery descriptions
 * 
 * This component displays patterns discovered from either:
 * - Semantic discovery (ML-based pattern detection)
 * - Conceptual labels discovery (based on discipline selection)
 * 
 * The component uses deferred rendering to improve initial load time.
 */
const DiscoveryDescriptionsReviewSection = ({
    descriptionsSuggestedBySemanticDiscovery,
    setDescriptionsSuggestedBySemanticDiscovery,
    createSemanticPatterns,
    projectCriteria,
}) => {
    const { token, layerId } = useParams()
    const versionId = useVersionId()
    const [deleteConceptualLabelDiscovery] = useDeleteConceptualLabelDiscoveryMutation()
    
    // UI state
    const showPlaceholders = usePageTransition()
    const [isFormSubmitting, setIsFormSubmitting] = useState(false)
    const [criteriaUpdating, setCriteriaUpdating] = useState([])
    const [selectedPatternIds, setSelectedPatternIds] = useState([])
    const [activeFilters, setActiveFilters] = useState({
        warningLevels: null,
        hasConceptualLabel: null
    })
    
    const [form] = Form.useForm()
    const navigate = useNavigate()

    // Helper function to format a pattern's data from form values
    const getPatternDataFromForm = useCallback((patternId) => {
        // Find the pattern in the descriptionsSuggestedBySemanticDiscovery array
        const semanticPattern = descriptionsSuggestedBySemanticDiscovery.find(
            pattern => pattern.id === patternId
        )
        
        if (!semanticPattern) return null
        
        // Get form values for this pattern
        return {
            patternId,
            name: form.getFieldValue(`name-${patternId}`),
            hit_feedback: form.getFieldValue(`hit-feedback-${patternId}`),
            miss_feedback: form.getFieldValue(`miss-feedback-${patternId}`),
            show_hit_reasoning: form.getFieldValue(`show-hit-reasoning-${patternId}`) || false,
            show_miss_reasoning: form.getFieldValue(`show-miss-reasoning-${patternId}`) || false,
            description: form.getFieldValue(`description-${patternId}`),
            criteria: semanticPattern.criteria.map(criterion => criterion.value),
            color_id: form.getFieldValue(`color-${patternId}`) ?? 0,
            warning_level: form.getFieldValue(`warning-level-${patternId}`) ?? 0,
            grade: form.getFieldValue(`grade-${patternId}`) ?? 0,
            grouping_cap: form.getFieldValue(`grouping-cap-${patternId}`) ?? 0,
            conceptual_labels: form.getFieldValue(`cl-${patternId}`) || []
        }
    }, [descriptionsSuggestedBySemanticDiscovery, form]);
    
    // Use the periodic saving hook
    const { isSaving, saveAllChangedPatterns } = usePeriodicSaving(
        form,
        descriptionsSuggestedBySemanticDiscovery,
        15000,
        getPatternDataFromForm
    );

    // Handler for pattern selection changes - use useCallback to prevent recreation
    const handlePatternSelectChange = useCallback((patternId, isSelected) => {
        setSelectedPatternIds(prevSelected => {
            if (isSelected) {
                return [...prevSelected, patternId];
            } else {
                return prevSelected.filter(id => id !== patternId);
            }
        });
    }, []);

    // Handle filter changes
    const handleFilterChange = useCallback((newFilters) => {
        setActiveFilters(newFilters);
    }, []);

    // Calculate projection cost only for selected patterns
    const criteriaListsForSelectedPatterns = useMemo(
        () => {
            if (selectedPatternIds.length === 0) {
                return [];
            }
            
            return descriptionsSuggestedBySemanticDiscovery
                ?.filter(pattern => selectedPatternIds.includes(pattern.id))
                .map(pattern => pattern.criteria.map(criterion => criterion.value));
        },
        [descriptionsSuggestedBySemanticDiscovery, selectedPatternIds],
    );
    
    const projectionCostDetails = useProjectionCostEstimation(
        criteriaListsForSelectedPatterns
    );
    
    const isAssignmentTooBigForProjection = projectionCostDetails === ASSIGNMENT_IS_TOO_BIG_FOR_PROJECTION
    const isEstimatingCost = projectionCostDetails === '...'

    // Redirect to home page if no patterns to display
    useEffect(() => {
        if (!descriptionsSuggestedBySemanticDiscovery?.length) {
            navigateToHomePage()
        }
    }, [descriptionsSuggestedBySemanticDiscovery, navigateToHomePage]);

    /**
     * Navigate back to the assignment home page
     */
    const navigateToHomePage = useCallback(() => {
        navigate({
            pathname: Routes.ASSIGNMENT_FULL.replace(':token', token)
                .replace(':section', AssignmentSections.HOME)
                .replace(':layerId', layerId),
            search: versionId ? `?${VERSION_ID_PARAM}=${versionId}` : '',
        })
    }, [navigate, token, layerId, versionId]);

    /**
     * Submit the form to create semantic patterns
     */
    const handleCreatePatterns = useCallback(async () => {
        setIsFormSubmitting(true)
        try {
            await saveAllChangedPatterns();
            
            // Only format and save selected patterns
            const patternsToSave = selectedPatternIds.length > 0 
                ? descriptionsSuggestedBySemanticDiscovery.filter(pattern => selectedPatternIds.includes(pattern.id))
                : descriptionsSuggestedBySemanticDiscovery;
                
            const patternIds = patternsToSave.map(({ id }) => id)
            
            const success = await createSemanticPatterns(token, layerId, patternIds)
            
            if (success) {
                navigateToHomePage()
            } else {
                setIsFormSubmitting(false)
            }
        } catch (error) {
            console.error('Error creating patterns:', error)
            setIsFormSubmitting(false)
        }
    }, [
        selectedPatternIds,
        descriptionsSuggestedBySemanticDiscovery,
        form,
        saveAllChangedPatterns,
        createSemanticPatterns,
        token,
        layerId,
        navigateToHomePage
    ]);

    /**
     * Update criteria fields for a specific pattern
     */
    const updateCriteriaFields = useCallback((patternId, updatedFields) => {
        const updatedPatterns = _.cloneDeep(descriptionsSuggestedBySemanticDiscovery)
        const targetPattern = updatedPatterns.find(pattern => pattern.id === patternId)
        
        if (targetPattern) {
            targetPattern.criteria = updatedFields
            setDescriptionsSuggestedBySemanticDiscovery(updatedPatterns)
        }
    }, [descriptionsSuggestedBySemanticDiscovery, setDescriptionsSuggestedBySemanticDiscovery]);

    /**
     * Remove a pattern suggestion 
     */
    const handleRemovePattern = useCallback(async (patternId) => {
        try {
            await deleteConceptualLabelDiscovery({ patternId })
            
            setSelectedPatternIds(prevSelected => prevSelected.filter(id => id !== patternId))
        } catch (error) {
            console.error('Error triggering pattern removal:', error)
        }
    }, [deleteConceptualLabelDiscovery]);

    /**
     * Regenerate and project criteria for a specific pattern
     */
    const handleRegenerateAndProjectCriteria = useCallback(async (patternId) => {
        setCriteriaUpdating(prev => [...prev, patternId])
        
        try {
            const pattern = descriptionsSuggestedBySemanticDiscovery.find(p => p.id === patternId)
            const criteriaValues = pattern.criteria.map(criterion => criterion.value)
            
            const projection = await projectCriteria(
                token, 
                criteriaValues, 
                LLM_VERSIONS.DEEP.value, 
                pattern.file_id
            )
            
            if (projection) {
                // Format the updated criteria with projection results
                const updatedCriteria = projection.map((p, index) => ({
                    id: String(index + 1),
                    value: p.criterion,
                    isMet: p.is_met,
                    reasoning: p.reasoning,
                    matchConfidenceInPercent: typeof p.match_confidence === 'number' 
                        ? p.match_confidence * 100 
                        : null,
                }))
                
                // Update criteria fields
                updateCriteriaFields(patternId, updatedCriteria)
                
                // Update projection in the semantic pattern
                const updatedPatterns = _.cloneDeep(descriptionsSuggestedBySemanticDiscovery)
                const patternToUpdate = updatedPatterns.find(p => p.id === patternId)
                
                if (patternToUpdate) {
                    patternToUpdate.projection = projection
                    setDescriptionsSuggestedBySemanticDiscovery(updatedPatterns)
                }
            }
        } finally {
            setCriteriaUpdating(prev => prev.filter(id => id !== patternId))
        }
    }, [
        descriptionsSuggestedBySemanticDiscovery,
        projectCriteria,
        token,
        updateCriteriaFields,
        setDescriptionsSuggestedBySemanticDiscovery
    ]);

    const handleContinueLater = useCallback(async () => {
        await saveAllChangedPatterns()
        navigateToHomePage()
    }, [saveAllChangedPatterns, navigateToHomePage]);

    return (
        <Form
            name='descriptions-review'
            autoComplete='off'
            form={form}
            onFinish={handleCreatePatterns}
            requiredMark={false}
            layout='vertical'
        >
            <Flex justify="end" className='mb-8'>
                <FilterPatterns onApplyFilters={handleFilterChange} />
            </Flex>
            <PatternList 
                patterns={descriptionsSuggestedBySemanticDiscovery}
                showPlaceholders={showPlaceholders}
                onRemove={handleRemovePattern}
                form={form}
                criteriaUpdating={criteriaUpdating}
                updateCriteriaFields={updateCriteriaFields}
                onRegenerateAndProject={handleRegenerateAndProjectCriteria}
                selectedPatternIds={selectedPatternIds}
                onPatternSelectChange={handlePatternSelectChange}
                filters={activeFilters}
            />
            {descriptionsSuggestedBySemanticDiscovery?.length > 0 && (
                <CreatePatternsButton
                    selectedPatternIds={selectedPatternIds}
                    projectionCostDetails={projectionCostDetails}
                    isFormSubmitting={isFormSubmitting}
                    isEstimatingCost={isEstimatingCost}
                    isSaving={isSaving}
                    isAssignmentTooBigForProjection={isAssignmentTooBigForProjection}
                    handleContinueLater={handleContinueLater}
                />
            )}
        </Form>
    )
}

const mapState = ({ assignment }) => ({
    descriptionsSuggestedBySemanticDiscovery: assignment.descriptionsSuggestedBySemanticDiscovery,
})

const mapDispatch = dispatch => ({
    createSemanticPatterns: (token, layerId, patternIds) => 
        dispatch(actions.createSemanticPatterns(token, layerId, patternIds)),
    setDescriptionsSuggestedBySemanticDiscovery: (value) => 
        dispatch(actions.setDescriptionsSuggestedBySemanticDiscovery(value)),
    projectCriteria: (token, criteria, llmVersion, submissionId) => 
        dispatch(actions.projectCriteria(token, criteria, false, llmVersion, submissionId)),
})

export default connect(mapState, mapDispatch)(DiscoveryDescriptionsReviewSection)
